import React, { useEffect, useState } from 'react';
import { X, RotateCcw, Star, Clock, Zap, CheckCircle, AlertCircle, ChevronDown, TrendingUp, User, Sparkles, Building2 } from 'lucide-react';
import { LineItem, SelectedDimensions, GLCodingResponse, DimensionValue } from './components/financial/gl-coding/types';
import { fetchGLCodingData } from './mockGLCodingAPI';
import { DimensionSelector } from './DimensionSelector';
import { useGLCodingHistory } from './useGLCodingHistory';
import { useUserContext } from './useUserContext';
import { useVendorDefaults } from './useVendorDefaults';
interface GLCodingDialogProps {
  lineItem: LineItem;
  onApply: (glCode: string, selectedDimensions: SelectedDimensions) => void;
  onClose: () => void;
}
export function GLCodingDialog({
  lineItem,
  onApply,
  onClose
}: GLCodingDialogProps) {
  const [loading, setLoading] = useState(true);
  const [glData, setGlData] = useState<GLCodingResponse | null>(null);
  const [selectedDimensions, setSelectedDimensions] = useState<SelectedDimensions>(lineItem.selectedDimensions || {});
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [fullCodeInput, setFullCodeInput] = useState('');
  const [fullCodeError, setFullCodeError] = useState('');
  const [fullCodeSuccess, setFullCodeSuccess] = useState(false);
  const [saveFavoriteMode, setSaveFavoriteMode] = useState(false);
  const [favoriteName, setFavoriteName] = useState('');
  const {
    recent,
    favorites,
    topUsed,
    addToRecent,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    getUsageCount
  } = useGLCodingHistory();
  const {
    currentUser
  } = useUserContext();
  const {
    getVendorDefault
  } = useVendorDefaults();
  // Get vendor default if available
  const vendorDefault = lineItem.vendor ? getVendorDefault(lineItem.vendor.id) : undefined;
  // Load GL Coding data on mount (single API call)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await fetchGLCodingData({
          selected: lineItem.selectedDimensions,
          vendorId: lineItem.vendor?.id,
          dimensionCount: lineItem.dimensionCount // Pass dimension count to API
        });
        setGlData(data);
        // Priority: Vendor defaults > User role defaults > Empty
        if (!lineItem.selectedDimensions) {
          if (vendorDefault?.defaultDimensions) {
            setSelectedDimensions(vendorDefault.defaultDimensions);
          } else if (currentUser?.defaultDimensions) {
            setSelectedDimensions(currentUser.defaultDimensions);
          }
        }
      } catch (error) {
        console.error('Failed to load GL Coding data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [lineItem.selectedDimensions, lineItem.dimensionCount, currentUser, vendorDefault]);
  // Parse full GL code entry
  const parseFullCode = (code: string) => {
    if (!glData || !code.trim()) {
      setFullCodeError('');
      setFullCodeSuccess(false);
      return;
    }
    const parts = code.split(/[.\-]/);
    if (parts.length !== glData.dimensionNames.length) {
      setFullCodeError(`Expected ${glData.dimensionNames.length} dimensions, got ${parts.length}. Format: ${glData.dimensionNames.join(' - ')}`);
      setFullCodeSuccess(false);
      return;
    }
    const newSelected: SelectedDimensions = {};
    let allValid = true;
    parts.forEach((part, index) => {
      const dimensionName = glData.dimensionNames[index];
      const trimmedPart = part.trim();
      if (trimmedPart) {
        newSelected[dimensionName] = trimmedPart;
      } else {
        allValid = false;
      }
    });
    if (!allValid) {
      setFullCodeError('All dimension values must be provided');
      setFullCodeSuccess(false);
      return;
    }
    setSelectedDimensions(newSelected);
    setFullCodeError('');
    setFullCodeSuccess(true);
  };
  const handleFullCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFullCodeInput(value);
    setFullCodeSuccess(false);
    setFullCodeError('');
  };
  const handleFullCodeBlur = () => {
    if (fullCodeInput.trim()) {
      parseFullCode(fullCodeInput);
    }
  };
  const handleFullCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      parseFullCode(fullCodeInput);
    }
  };
  // Get available values for a dimension based on previous selections
  const getAvailableValues = (dimensionName: string): DimensionValue[] => {
    if (!glData) return [];
    const dimensionData = glData.dimensionData[dimensionName];
    if (!dimensionData) return [];
    if (!dimensionData.validCombinations) {
      return dimensionData.values;
    }
    const currentIndex = glData.dimensionNames.indexOf(dimensionName);
    if (currentIndex === 0) {
      return dimensionData.values;
    }
    const previousDimensionName = glData.dimensionNames[currentIndex - 1];
    const previousValue = selectedDimensions[previousDimensionName];
    if (!previousValue) {
      return [];
    }
    const validCodes = dimensionData.validCombinations[previousValue] || [];
    return dimensionData.values.filter(v => validCodes.includes(v.code));
  };
  // Handle dimension selection
  const handleDimensionChange = (dimensionName: string, value: string) => {
    const newSelected = {
      ...selectedDimensions
    };
    newSelected[dimensionName] = value;
    if (glData) {
      const currentIndex = glData.dimensionNames.indexOf(dimensionName);
      for (let i = currentIndex + 1; i < glData.dimensionNames.length; i++) {
        delete newSelected[glData.dimensionNames[i]];
      }
    }
    setSelectedDimensions(newSelected);
    setFullCodeSuccess(false);
  };
  // Assemble GL Code from selected dimensions
  const assembleGLCode = (): string => {
    if (!glData) return '';
    const codes = glData.dimensionNames.map(name => selectedDimensions[name]).filter(Boolean);
    return codes.join(glData.separator);
  };
  // Check if all dimensions are selected
  const isComplete = glData ? glData.dimensionNames.every(name => selectedDimensions[name]) : false;
  // Get current step (first incomplete dimension)
  const currentStep = glData ? glData.dimensionNames.findIndex(name => !selectedDimensions[name]) : -1;
  const glCode = assembleGLCode();
  const isCurrentCodeFavorite = isFavorite(glCode);
  const handleApply = () => {
    if (isComplete) {
      addToRecent(selectedDimensions, glCode);
      onApply(glCode, selectedDimensions);
    }
  };
  const handleClearAll = () => {
    setSelectedDimensions({});
    setFullCodeInput('');
    setFullCodeError('');
    setFullCodeSuccess(false);
  };
  const handleLoadSuggestion = (dimensions: SelectedDimensions) => {
    // Filter dimensions to only include those that exist in current glData
    if (glData) {
      const filteredDimensions: SelectedDimensions = {};
      glData.dimensionNames.forEach(dimensionName => {
        if (dimensions[dimensionName]) {
          filteredDimensions[dimensionName] = dimensions[dimensionName];
        }
      });
      setSelectedDimensions(filteredDimensions);
    } else {
      setSelectedDimensions(dimensions);
    }
    setFullCodeSuccess(false);
  };
  const handleSaveFavorite = () => {
    if (favoriteName.trim() && isComplete) {
      addToFavorites(favoriteName, selectedDimensions, glCode);
      setFavoriteName('');
      setSaveFavoriteMode(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };
  const completedCount = glData ? glData.dimensionNames.filter(name => selectedDimensions[name]).length : 0;
  const totalCount = glData ? glData.dimensionNames.length : 0;
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose} onKeyDown={handleKeyDown} data-id="element-22861">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col animate-fade-in" onClick={e => e.stopPropagation()} data-id="element-22862">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white" data-id="element-22863">
          <div className="flex items-start justify-between" data-id="element-22864">
            <div className="flex-1" data-id="element-22865">
              <div className="flex items-center gap-3 flex-wrap" data-id="element-22866">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-22867">
                  GL Coding
                </h2>
                {lineItem.vendor && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-100 rounded-full" data-id="element-22868">
                    <Building2 size={14} className="text-teal-700" data-id="element-22869" />
                    <span className="text-xs font-medium text-teal-700" data-id="element-22870">
                      {lineItem.vendor.name}
                    </span>
                  </div>}
                {currentUser && <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 rounded-full" data-id="element-22871">
                    <User size={14} className="text-blue-700" data-id="element-22872" />
                    <span className="text-xs font-medium text-blue-700" data-id="element-22873">
                      {currentUser.name} • {currentUser.role}
                    </span>
                  </div>}
              </div>
              <p className="text-sm text-gray-600 mt-0.5" data-id="element-22874">
                {lineItem.description}
              </p>
              <p className="text-xs text-gray-500 mt-2" data-id="element-22875">
                Select financial dimensions to generate the GL code for this
                invoice line
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors ml-4" aria-label="Close dialog" data-id="element-22876">
              <X size={20} data-id="element-22877" />
            </button>
          </div>

          {/* Progress Indicator */}
          {glData && <div className="mt-4" data-id="element-22878">
              <div className="flex items-center justify-between text-sm mb-2" data-id="element-22879">
                <span className="font-medium text-gray-700" data-id="element-22880">
                  Step {Math.min(completedCount + 1, totalCount)} of{' '}
                  {totalCount}
                </span>
                <span className="text-gray-600" data-id="element-22881">
                  {completedCount} / {totalCount} completed
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden" data-id="element-22882">
                <div className="bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out" style={{
              width: `${completedCount / totalCount * 100}%`
            }} data-id="element-22883" />
              </div>
            </div>}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-22884">
          {loading ? <div className="flex items-center justify-center py-12" data-id="element-22885">
              <div className="text-center" data-id="element-22886">
                <div className="inline-block w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" data-id="element-22887"></div>
                <p className="text-sm text-gray-600" data-id="element-22888">Loading dimensions...</p>
              </div>
            </div> : glData ? <div className="space-y-6" data-id="element-22889">
              {/* Full Code Entry Section */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border-2 border-amber-200" data-id="element-22890">
                <div className="flex items-center gap-2 mb-3" data-id="element-22891">
                  <Zap size={18} className="text-amber-600" data-id="element-22892" />
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-22893">
                    Quick Entry
                  </h3>
                  <span className="text-xs text-gray-600 ml-auto" data-id="element-22894">
                    For experienced users
                  </span>
                </div>

                <div className="relative" data-id="element-22895">
                  <input type="text" value={fullCodeInput} onChange={handleFullCodeChange} onBlur={handleFullCodeBlur} onKeyDown={handleFullCodeKeyDown} placeholder="Enter complete GL code (e.g., 100-1010-P004-A200-APAC or 100.1010.P004.A200.APAC)" className="w-full px-4 py-2.5 pr-10 border-2 border-amber-300 rounded-lg bg-white text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent
                             placeholder:text-gray-400 placeholder:font-sans" data-id="element-22896" />
                  {fullCodeSuccess && <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" data-id="element-22897" />}
                  {fullCodeError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" data-id="element-22898" />}
                </div>

                <div className="mt-2 flex items-start gap-2" data-id="element-22899">
                  {fullCodeSuccess ? <p className="text-xs text-green-700 flex items-center gap-1" data-id="element-22900">
                      <CheckCircle size={14} data-id="element-22901" />
                      GL code parsed successfully! All dimensions populated.
                    </p> : fullCodeError ? <p className="text-xs text-red-700 flex items-start gap-1" data-id="element-22902">
                      <AlertCircle size={14} className="flex-shrink-0 mt-0.5" data-id="element-22903" />
                      <span data-id="element-22904">{fullCodeError}</span>
                    </p> : <p className="text-xs text-gray-600" data-id="element-22905">
                      Use "-" or "." as separators. Press Enter or click away to
                      parse.
                    </p>}
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3" data-id="element-22906">
                <div className="flex-1 border-t border-gray-300" data-id="element-22907"></div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-22908">
                  Or select step by step
                </span>
                <div className="flex-1 border-t border-gray-300" data-id="element-22909"></div>
              </div>

              {/* Smart Suggestions Section */}
              {(vendorDefault || currentUser?.defaultDimensions || favorites.length > 0 || topUsed.length > 0) && <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border-2 border-purple-200" data-id="element-22910">
                  <button onClick={() => setShowSuggestions(!showSuggestions)} className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors w-full" data-id="element-22911">
                    <Sparkles size={18} className="text-purple-600" data-id="element-22912" />
                    Smart Suggestions
                    <span className="text-xs text-gray-600 ml-auto mr-2" data-id="element-22913">
                      Personalized for you
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`} data-id="element-22914" />
                  </button>

                  {showSuggestions && <div className="mt-4 space-y-4" data-id="element-22915">
                      {/* Vendor Defaults - Highest Priority */}
                      {vendorDefault && <div data-id="element-22916">
                          <div className="flex items-center gap-1.5 mb-2" data-id="element-22917">
                            <Building2 size={14} className="text-teal-600" data-id="element-22918" />
                            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-22919">
                              Vendor Default Configuration
                            </h4>
                          </div>
                          <button onClick={() => handleLoadSuggestion(vendorDefault.defaultDimensions)} className="w-full text-left px-4 py-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border-2 border-teal-300 hover:border-teal-500 hover:shadow-md transition-all group" data-id="element-22920">
                            <div className="flex items-start justify-between gap-3" data-id="element-22921">
                              <div className="flex-1" data-id="element-22922">
                                <div className="flex items-center gap-2 mb-1" data-id="element-22923">
                                  <div className="text-sm font-bold text-teal-900 group-hover:text-teal-700" data-id="element-22924">
                                    {vendorDefault.vendorName}
                                  </div>
                                  <span className="px-2 py-0.5 bg-teal-200 text-teal-800 text-xs font-medium rounded" data-id="element-22925">
                                    {vendorDefault.category}
                                  </span>
                                </div>
                                <div className="text-xs text-teal-700 mb-2" data-id="element-22926">
                                  {vendorDefault.description}
                                </div>
                                <div className="flex flex-wrap gap-1.5" data-id="element-22927">
                                  {glData && Object.entries(vendorDefault.defaultDimensions).filter(([key]) => glData.dimensionNames.includes(key)).map(([key, value]) => <span key={key} className="px-2 py-0.5 bg-white text-teal-900 text-xs rounded border border-teal-200" data-id="element-22928">
                                          {key}: {value}
                                        </span>)}
                                </div>
                              </div>
                              <Sparkles size={18} className="text-teal-500 group-hover:text-teal-600 flex-shrink-0" data-id="element-22929" />
                            </div>
                          </button>
                        </div>}

                      {/* Role-based defaults */}
                      {currentUser?.defaultDimensions && <div data-id="element-22930">
                          <div className="flex items-center gap-1.5 mb-2" data-id="element-22931">
                            <User size={14} className="text-indigo-600" data-id="element-22932" />
                            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-22933">
                              Your Role Defaults
                            </h4>
                          </div>
                          <button onClick={() => handleLoadSuggestion(currentUser.defaultDimensions!)} className="w-full text-left px-3 py-2.5 bg-white rounded-lg border border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 transition-all group" data-id="element-22934">
                            <div className="flex items-center justify-between" data-id="element-22935">
                              <div data-id="element-22936">
                                <div className="text-sm font-medium text-gray-900 group-hover:text-indigo-700" data-id="element-22937">
                                  {currentUser.role} Preset
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5" data-id="element-22938">
                                  {glData && Object.entries(currentUser.defaultDimensions).filter(([key]) => glData.dimensionNames.includes(key)).map(([key, value], index, arr) => <span key={key} data-id="element-22939">
                                          {key}: {value}
                                          {index < arr.length - 1 ? ' • ' : ''}
                                        </span>)}
                                </div>
                              </div>
                              <Zap size={16} className="text-indigo-400 group-hover:text-indigo-600" data-id="element-22940" />
                            </div>
                          </button>
                        </div>}

                      {/* Favorites */}
                      {favorites.length > 0 && <div data-id="element-22941">
                          <div className="flex items-center gap-1.5 mb-2" data-id="element-22942">
                            <Star size={14} className="text-amber-600 fill-amber-600" data-id="element-22943" />
                            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-22944">
                              My Favorites
                            </h4>
                          </div>
                          <div className="space-y-2" data-id="element-22945">
                            {favorites.slice(0, 5).map(fav => <div key={fav.id} className="flex items-center gap-2" data-id="element-22946">
                                <button onClick={() => handleLoadSuggestion(fav.dimensions)} className="flex-1 text-left px-3 py-2 bg-white rounded-lg border border-amber-200 hover:border-amber-400 hover:bg-amber-50 transition-all group" data-id="element-22947">
                                  <div className="flex items-center justify-between" data-id="element-22948">
                                    <div className="flex-1 min-w-0" data-id="element-22949">
                                      <div className="text-sm font-medium text-gray-900 group-hover:text-amber-700" data-id="element-22950">
                                        {fav.name}
                                      </div>
                                      <code className="text-xs font-mono text-gray-600" data-id="element-22951">
                                        {fav.glCode}
                                      </code>
                                    </div>
                                  </div>
                                </button>
                                <button onClick={() => removeFromFavorites(fav.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" title="Remove from favorites" data-id="element-22952">
                                  <X size={16} data-id="element-22953" />
                                </button>
                              </div>)}
                          </div>
                        </div>}

                      {/* Top Used */}
                      {topUsed.length > 0 && <div data-id="element-22954">
                          <div className="flex items-center gap-1.5 mb-2" data-id="element-22955">
                            <TrendingUp size={14} className="text-green-600" data-id="element-22956" />
                            <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-22957">
                              My Top 10 Used
                            </h4>
                          </div>
                          <div className="space-y-2" data-id="element-22958">
                            {topUsed.slice(0, 5).map((item, index) => <button key={item.glCode} onClick={() => handleLoadSuggestion(item.dimensions)} className="w-full text-left px-3 py-2 bg-white rounded-lg border border-green-200 hover:border-green-400 hover:bg-green-50 transition-all group" data-id="element-22959">
                                <div className="flex items-center justify-between" data-id="element-22960">
                                  <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-22961">
                                    <span className="text-xs font-bold text-green-600 w-5" data-id="element-22962">
                                      #{index + 1}
                                    </span>
                                    <code className="text-xs font-mono text-gray-900 group-hover:text-green-700" data-id="element-22963">
                                      {item.glCode}
                                    </code>
                                  </div>
                                  <span className="text-xs text-gray-500" data-id="element-22964">
                                    Used {item.useCount}x
                                  </span>
                                </div>
                              </button>)}
                          </div>
                        </div>}
                    </div>}
                </div>}

              {/* Dynamic dimension selectors */}
              <div className="space-y-4" data-id="element-22965">
                {glData.dimensionNames.map((dimensionName, index) => {
              const availableValues = getAvailableValues(dimensionName);
              const previousDimensionName = index > 0 ? glData.dimensionNames[index - 1] : null;
              const isPreviousSelected = !previousDimensionName || selectedDimensions[previousDimensionName];
              const isComplete = !!selectedDimensions[dimensionName];
              const isCurrent = index === currentStep;
              return <DimensionSelector key={dimensionName} dimensionName={dimensionName} values={availableValues} selectedValue={selectedDimensions[dimensionName]} onChange={value => handleDimensionChange(dimensionName, value)} disabled={!isPreviousSelected || availableValues.length === 0} stepNumber={index + 1} isComplete={isComplete} isCurrent={isCurrent} required={true} tooltip={`Select the ${dimensionName} for this transaction`} data-id="element-22966" />;
            })}
              </div>

              {/* GL Code Summary Panel */}
              <div className="mt-6 pt-6 border-t-2 border-gray-200" data-id="element-22967">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200" data-id="element-22968">
                  <div className="flex items-center justify-between mb-3" data-id="element-22969">
                    <label className="text-sm font-semibold text-gray-900" data-id="element-22970">
                      GL Code Summary
                    </label>
                    {isComplete && !isCurrentCodeFavorite && <button onClick={() => setSaveFavoriteMode(!saveFavoriteMode)} className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors" data-id="element-22971">
                        <Star size={14} data-id="element-22972" />
                        Save as Favorite
                      </button>}
                  </div>

                  {saveFavoriteMode && <div className="mb-3 flex gap-2" data-id="element-22973">
                      <input type="text" value={favoriteName} onChange={e => setFavoriteName(e.target.value)} placeholder="Enter favorite name..." className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-amber-500" onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleSaveFavorite();
                  }
                }} data-id="element-22974" />
                      <button onClick={handleSaveFavorite} disabled={!favoriteName.trim()} className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors" data-id="element-22975">
                        Save
                      </button>
                      <button onClick={() => {
                  setSaveFavoriteMode(false);
                  setFavoriteName('');
                }} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors" data-id="element-22976">
                        Cancel
                      </button>
                    </div>}

                  {completedCount > 0 ? <div className="space-y-3" data-id="element-22977">
                      {/* Dimension breakdown */}
                      <div className="space-y-1.5" data-id="element-22978">
                        {glData.dimensionNames.map(name => {
                    const value = selectedDimensions[name];
                    if (!value) return null;
                    const dimensionData = glData.dimensionData[name];
                    const option = dimensionData?.values.find(v => v.code === value);
                    return <div key={name} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2" data-id="element-22979">
                              <span className="text-gray-600 font-medium" data-id="element-22980">
                                {name}:
                              </span>
                              <span className="text-gray-900 font-mono" data-id="element-22981">
                                {option ? `${option.code} - ${option.description}` : value}
                              </span>
                            </div>;
                  })}
                      </div>

                      {/* Final GL Code */}
                      <div className="bg-white rounded-lg px-4 py-3 border-2 border-blue-300" data-id="element-22982">
                        <div className="text-xs font-medium text-gray-600 mb-1" data-id="element-22983">
                          Complete GL Code:
                        </div>
                        <code className="text-lg font-mono font-bold text-blue-600" data-id="element-22984">
                          {glCode}
                        </code>
                      </div>
                    </div> : <p className="text-sm text-gray-600 italic" data-id="element-22985">
                      Select dimensions above to generate your GL code
                    </p>}
                </div>
              </div>
            </div> : <div className="text-center py-12 text-gray-600" data-id="element-22986">
              Failed to load dimension data
            </div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-22987">
          <button onClick={handleClearAll} disabled={completedCount === 0 && !fullCodeInput} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded
                     hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-22988">
            <RotateCcw size={16} data-id="element-22989" />
            Clear All
          </button>

          <div className="flex items-center gap-3" data-id="element-22990">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded
                       hover:bg-gray-50 transition-colors" data-id="element-22991">
              Cancel
            </button>
            <button onClick={handleApply} disabled={!isComplete} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded
                       hover:bg-blue-700 transition-colors
                       disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300
                       shadow-sm hover:shadow" data-id="element-22992">
              Apply GL Code
            </button>
          </div>
        </div>
      </div>
    </div>;
}